interface PieChartProps {
  data: { label: string; value: number; color?: string }[];
  size?: number;
}

const DEFAULT_COLORS = [
  '#f59e0b', // ember-bright
  '#10b981', // success green
  '#3b82f6', // blue
  '#ec4899', // pink
  '#8b5cf6', // purple
  '#14b8a6', // teal
  '#f97316', // orange
  '#06b6d4', // cyan
];

export function PieChart({ data, size = 200 }: PieChartProps) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  const center = size / 2;
  const radius = size / 2 - 10;

  let currentAngle = -90; // Start from top

  const slices = data.map((d, i) => {
    const percentage = d.value / total;
    const angle = percentage * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    // Calculate arc path
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    const pathD = [
      `M ${center} ${center}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
      'Z',
    ].join(' ');

    const color = d.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length];

    return (
      <path
        key={i}
        d={pathD}
        fill={color}
        stroke="var(--charcoal-mid)"
        strokeWidth="2"
      />
    );
  });

  return (
    <div className="pie-chart-container">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {slices}
      </svg>
      <div className="pie-chart-legend">
        {data.map((d, i) => (
          <div key={i} className="legend-item">
            <span
              className="legend-color"
              style={{ background: d.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length] }}
            />
            <span className="legend-label">{d.label}</span>
            <span className="legend-value">{((d.value / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
